import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../common/Button';
import { MemberDTO } from '../../../services/apiClient';
import FormField from '../../common/FormField';
import handleChange from '../../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../../hooks/useFormValidation';
import PasswordRequirements from '../../common/PasswordRequirements';

interface Props {
    member: MemberDTO;
    onChange: (updated: Partial<MemberDTO>) => void;
    onSubmit: () => void;
    onBack: () => void;
    isLoading?: boolean;
}

interface AccountSetupForm extends MemberDTO {
    confirmPassword?: string; // local-only
}

const getPasswordValidation = (pwd: string) => ({
    length: (pwd ?? '').length >= 6,
    uppercase: /[A-Z]/.test(pwd ?? ''),
    lowercase: /[a-z]/.test(pwd ?? ''),
    digit: /\d/.test(pwd ?? ''),
});

const AccountSetup: React.FC<Props> = ({ member, onChange, onSubmit, onBack, isLoading }) => {
    const [formData, setFormData] = useState<AccountSetupForm>({ ...member, confirmPassword: '' });
    const policyLinks = [
        { name: 'User Agreement', url: 'https://www.natshoot.co.za/wp-content/uploads/2025/02/NS_User-Agreement_2025.pdf' },
        { name: 'Privacy Policy', url: 'https://www.natshoot.co.za/wp-content/uploads/2025/02/NS_Privacy-Policy_2025.pdf' },
        { name: 'Disciplinary Policy', url: 'https://natshoot.co.za/wp-content/uploads/2026/04/Natshoot-Code-of-Discipline.pdf' },
        { name: 'Cookie Policy', url: 'https://www.natshoot.co.za/wp-content/uploads/2025/02/NS_Cookie-Policy_2025.pdf' },
    ];
    const policyLinkClasses = 'text-primary font-medium hover:underline hover:text-primary';

    const pwReqs = useMemo(() => getPasswordValidation(formData.password || ''), [formData.password]);
    const allPwReqsMet = pwReqs.length && pwReqs.uppercase && pwReqs.lowercase && pwReqs.digit;
    const passwordsMatch =
        (formData.confirmPassword ?? '') === (formData.password ?? '') && (formData.confirmPassword ?? '') !== '';

    const validationRules: ValidationRules = {
        password: [
            { required: true, message: 'Password is required' },
            { custom: (v) => getPasswordValidation(v || '').length, message: 'Password must be at least 6 characters' },
            { custom: (v) => getPasswordValidation(v || '').uppercase, message: 'Password needs an uppercase letter' },
            { custom: (v) => getPasswordValidation(v || '').lowercase, message: 'Password needs a lowercase letter' },
            { custom: (v) => getPasswordValidation(v || '').digit, message: 'Password needs a digit' },
        ],
        confirmPassword: [
            { required: true, message: 'Please confirm your password' },
            { custom: () => passwordsMatch, message: 'Passwords do not match' },
        ],
    };

    const { errors, validate } = useFormValidation<AccountSetupForm>(validationRules);

    const handleSubmit = () => {
        if (validate(formData)) onSubmit();
    };

    return (
        <div>
            <PasswordRequirements validation={pwReqs} />
            <FormField
                id="password"
                label="Password"
                type="password"
                value={formData.password || ''}
                onChange={(e) => handleChange(e, setFormData, onChange)} // send upstream
                placeholder="Enter your password"
                required={true}
                error={errors.password}
            />
            <FormField
                id="confirmPassword"
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword || ''}
                onChange={(e) => handleChange(e, setFormData)} // local only; don't send upstream
                placeholder="Confirm your password"
                required={true}
                error={errors.confirmPassword}
            />

            {formData.confirmPassword ? (
                <div className={`text-sm mt-1 ${passwordsMatch ? 'text-emerald-600' : 'text-red-600'}`}>
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </div>
            ) : null}

            <div>
                <span className='text-slate-400 text-sm'>By clicking Sign up, you agree to our <a className={policyLinkClasses} href={policyLinks[0].url} target="_blank" rel="noopener noreferrer">{policyLinks[0].name}</a>, <a className={policyLinkClasses} href={policyLinks[1].url} target="_blank" rel="noopener noreferrer">{policyLinks[1].name}</a>, <a className={policyLinkClasses} href={policyLinks[2].url} target="_blank" rel="noopener noreferrer">{policyLinks[2].name}</a> and <a className={policyLinkClasses} href={policyLinks[3].url} target="_blank" rel="noopener noreferrer">{policyLinks[3].name}</a>.</span>
            </div>

            <div className="mt-4 flex justify-between w-full">
                <Button onClick={onBack} variant="secondary">
                    Back
                </Button>
                <Button onClick={handleSubmit} variant="primary" isLoading={isLoading}>
                    Sign up
                </Button>
            </div>
        </div>
    );
};

export default AccountSetup;
